import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonRouterLink,
  IonTitle,
  IonToolbar,
  ViewWillEnter,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import {
  getCreateInteractionNoteDtoFormGroup,
  WorkCreateInteractionDto,
  WorkDocument,
  WorkDocumentsV2Service,
  WorkDocumenttypeIdentifierNameEnum,
  WorkInteractionRelationTypeEnum,
  WorkInteractionsV2Service,
  WorkPermissionsEnum,
  WorkProspect,
  WorkProspectStatusEnum,
  WorkProspectsV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { BadgeComponent, ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { GetFullnameOfContactPipe, TelHrefPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { GalleryComponent } from '@work-frontend-monorepo/work-ui/rich-content';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { TimelineComponent } from '@work-frontend-monorepo/work-ui/visualization';
import { ProspectStatusBadgePipe } from '../../../../../../../work-office/src/app/features/akquiseobjekte/pipes/prospect-status-badge.pipe';

/**
 * Akquiseobjekt unterwegs: Adresse, Ansprechperson, Notizen und Fotos/Videos (mit Beschriften).
 * Die Umwandlung in ein Projekt bleibt im work-office — dort gehören Kontakt, Containertyp und Kundenberater hin.
 */
@Component({
  selector: 'sfg-app-akquise-details-page',
  templateUrl: './akquise-details-page.component.html',
  imports: [
    BadgeComponent,
    ButtonComponent,
    GalleryComponent,
    GetFullnameOfContactPipe,
    InputFieldComponent,
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonRouterLink,
    IonTitle,
    IonToolbar,
    ProspectStatusBadgePipe,
    ReactiveFormsModule,
    RouterLink,
    TelHrefPipe,
    TimelineComponent,
    TranslatePipe,
  ],
})
export class AkquiseDetailsPageComponent implements OnInit, ViewWillEnter {
  /** Feuert `ionViewWillEnter` doch beim ersten Eintritt, hat `ngOnInit` gerade eben geladen. */
  private static readonly SKIP_ENTER_AFTER_INIT_MS = 1500;

  private readonly prospectsV2Service = inject(WorkProspectsV2Service);
  private readonly documentsV2Service = inject(WorkDocumentsV2Service);
  private readonly interactionsV2Service = inject(WorkInteractionsV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store);

  private readonly timeline = viewChild<TimelineComponent>('timeline');

  private readonly hasUpdatePermission = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.ProspectUPDATE)));
  private readonly hasInteractionCreatePermission = toSignal(
    this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.InteractionCREATE)),
  );

  protected readonly prospect = signal<WorkProspect | undefined>(undefined);
  protected readonly documents = signal<WorkDocument[]>([]);
  protected readonly isOpen = computed(() => this.prospect()?.status === WorkProspectStatusEnum.Open);
  protected readonly canUpdate = computed(() => this.isOpen() && !!this.hasUpdatePermission());
  protected readonly canAddNote = computed(() => this.isOpen() && !!this.hasInteractionCreatePermission());
  protected readonly hasContactPerson = computed(() => {
    const prospect = this.prospect();
    return !!(prospect?.company || prospect?.firstName || prospect?.lastName || prospect?.phone || prospect?.email);
  });

  protected readonly noteFormGroup = getCreateInteractionNoteDtoFormGroup();
  protected readonly noteSaveLoadingState = signal<LoadingAndFeedbackState>('none');

  protected readonly WorkInteractionRelationTypeEnum = WorkInteractionRelationTypeEnum;
  protected readonly WorkDocumenttypeIdentifierNameEnum = WorkDocumenttypeIdentifierNameEnum;

  private initializedAt = 0;

  /** Ionic feuert `ionViewWillEnter` beim direkten Aufruf nicht verlässlich — der erste Ladevorgang läuft darum hier. */
  async ngOnInit(): Promise<void> {
    this.initializedAt = Date.now();
    await this.load();
  }

  /** Beim Zurückkehren (z. B. nach dem Bearbeiten) neu laden; direkt nach `ngOnInit` nicht doppelt. */
  async ionViewWillEnter(): Promise<void> {
    if (Date.now() - this.initializedAt < AkquiseDetailsPageComponent.SKIP_ENTER_AFTER_INIT_MS) {
      return;
    }
    await this.load();
  }

  private async load(): Promise<void> {
    const prospectId = Number(this.activatedRoute.snapshot.params['prospectId']);
    if (!prospectId) {
      return;
    }
    await Promise.all([this.loadProspect(prospectId), this.loadDocuments()]);
    await this.timeline()?.loadInteractionsHistory(WorkInteractionRelationTypeEnum.Prospects, prospectId);
  }

  protected openMaps(): void {
    const prospect = this.prospect();
    if (!prospect) {
      return;
    }
    const query =
      prospect.latitude != null && prospect.longitude != null
        ? `${prospect.latitude},${prospect.longitude}`
        : [prospect.objectStreet, `${prospect.objectZip ?? ''} ${prospect.objectCity ?? ''}`.trim()].filter(Boolean).join(', ');
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_system');
  }

  protected async saveNote(): Promise<void> {
    const prospectId = this.prospect()?.id;
    if (!prospectId || this.noteFormGroup.invalid || this.noteSaveLoadingState() === 'loading') {
      return;
    }
    this.noteSaveLoadingState.set('loading');
    try {
      await lastValueFrom(this.interactionsV2Service.interactionsV2ControllerCreate({ body: this.noteFormGroup.value as WorkCreateInteractionDto }));
      this.noteSaveLoadingState.set('success');
      this.noteFormGroup.reset({
        interactiontypeId: 1,
        notes: '',
        visibleForCustomer: false,
        relationType: WorkInteractionRelationTypeEnum.Prospects,
        relationTypeId: prospectId,
      });
      await this.timeline()?.loadInteractionsHistory(WorkInteractionRelationTypeEnum.Prospects, prospectId);
    } catch (createNoteException) {
      this.noteSaveLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('createNoteException', createNoteException);
    }
  }

  protected async loadDocuments(): Promise<void> {
    const prospectId = Number(this.activatedRoute.snapshot.params['prospectId']);
    try {
      const paginated = await lastValueFrom(
        this.documentsV2Service.documentsV2ControllerFindAllPaginated({
          'filter.prospectId': [`$eq:${prospectId}`],
          'filter.active': ['$eq:1'],
          limit: 500,
        }),
      );
      this.documents.set(paginated.data ?? []);
    } catch (loadDocumentsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadDocumentsException', loadDocumentsException);
    }
  }

  private async loadProspect(prospectId: number): Promise<void> {
    try {
      this.prospect.set(await lastValueFrom(this.prospectsV2Service.prospectsV2ControllerFindOneById({ id: prospectId })));
      // Pflichtfelder der Notiz — ohne sie bleibt das Formular ungültig und der Speichern-Knopf gesperrt.
      this.noteFormGroup.patchValue({ relationType: WorkInteractionRelationTypeEnum.Prospects, relationTypeId: prospectId });
    } catch (loadProspectException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProspectException', loadProspectException);
    }
  }
}
